import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import TelLink from "@/components/TelLink";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { MapPin, CheckCircle } from "lucide-react";

const serviceAreas = [
  { name: "Airport West", slug: "airport-west" },
  { name: "Attwood", slug: "attwood" },
  { name: "Broadmeadows", slug: "broadmeadows" },
  { name: "Brunswick", slug: "brunswick" },
  { name: "Brunswick West", slug: "brunswick-west" },
  { name: "Bundoora", slug: "bundoora" },
  { name: "Campbellfield", slug: "campbellfield" },
  { name: "Coburg", slug: "coburg" },
  { name: "Coburg North", slug: "coburg-north" },
  { name: "Coolaroo", slug: "coolaroo" },
  { name: "Craigieburn", slug: "craigieburn" },
  { name: "Dallas", slug: "dallas" },
  { name: "Essendon Fields", slug: "essendon-fields" },
  { name: "Essendon North", slug: "essendon-north" },
  { name: "Fawkner", slug: "fawkner" },
  { name: "Gladstone Park", slug: "gladstone-park" },
  { name: "Glenroy", slug: "glenroy" },
  { name: "Gowanbrae", slug: "gowanbrae" },
  { name: "Greensborough", slug: "greensborough" },
  { name: "Greenvale", slug: "greenvale" },
  { name: "Hadfield", slug: "hadfield" },
  { name: "Heidelberg Heights", slug: "heidelberg-heights" },
  { name: "Heidelberg West", slug: "heidelberg-west" },
  { name: "Ivanhoe", slug: "ivanhoe" },
  { name: "Ivanhoe East", slug: "ivanhoe-east" },
  { name: "Jacana", slug: "jacana" },
  { name: "Kalkallo", slug: "kalkallo" },
  { name: "Lalor", slug: "lalor" },
  { name: "Macleod", slug: "macleod" },
  { name: "Meadow Heights", slug: "meadow-heights" },
  { name: "Mickleham", slug: "mickleham" },
  { name: "Niddrie", slug: "niddrie" },
  { name: "Oak Park", slug: "oak-park" },
  { name: "Pascoe Vale", slug: "pascoe-vale" },
  { name: "Pascoe Vale South", slug: "pascoe-vale-south" },
  { name: "Preston", slug: "preston" },
  { name: "Reservoir", slug: "reservoir" },
  { name: "Roxburgh Park", slug: "roxburgh-park" },
  { name: "Somerton", slug: "somerton" },
  { name: "Strathmore", slug: "strathmore" },
  { name: "Strathmore Heights", slug: "strathmore-heights" },
  { name: "Thomastown", slug: "thomastown" },
  { name: "Thornbury", slug: "thornbury" },
  { name: "Tullamarine", slug: "tullamarine" },
  { name: "Upfield", slug: "upfield" },
  { name: "Viewbank", slug: "viewbank" },
  { name: "Westmeadows", slug: "westmeadows" },
  { name: "Yallambie", slug: "yallambie" }
];

const ServiceAreasPage = () => {
  useScrollAnimation();

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Service Areas | Y3 Smiles Dental" description="Y3 Smiles Dental serves Broadmeadows and surrounding suburbs — Craigieburn, Roxburgh Park, Glenroy, Greenvale and more across Melbourne's north." image="/og-shopfront.jpg" />
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-neutral-50 via-primary/5 to-neutral-100 py-32 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary"></div>
            <div className="absolute top-40 right-20 w-20 h-20 rounded-full bg-secondary"></div>
            <div className="absolute bottom-20 left-1/4 w-16 h-16 rounded-full bg-primary"></div>
            <div className="absolute bottom-32 right-1/3 w-24 h-24 rounded-full bg-secondary"></div>
          </div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center mb-8 p-4 bg-white/90 backdrop-blur-sm rounded-2xl shadow-soft">
                  <MapPin className="w-10 h-10 mr-4 text-primary" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-primary uppercase tracking-wider">Service Coverage</p>
                    <p className="text-lg font-bold text-neutral-800">Melbourne Northern Suburbs</p>
                  </div>
                </div>

                <h1 className="text-6xl md:text-7xl font-bold text-neutral-800 mb-6 leading-tight">
                  Areas We{' '}
                  <span className="block text-primary">Proudly Serve</span>
                </h1>

                <p className="text-xl text-neutral-800 mb-12 leading-relaxed max-w-3xl mx-auto">
                  Y3 Smiles Dental is conveniently located in Broadmeadows, serving patients from across Melbourne's northern suburbs. 
                  Our modern facility is easily accessible to residents throughout the region.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Location Info */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 md:p-12 shadow-soft mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6">
                  Our Convenient Broadmeadows Location
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <MapPin className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-neutral-800">Address:</p>
                      <p className="text-neutral-700">Unit 1, 34 King William Street, Broadmeadows VIC 3047</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-neutral-800">Easy Access:</p>
                      <p className="text-neutral-700">Ample parking available, close to public transport, wheelchair accessible</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-neutral-800">Phone:</p>
                      <p className="text-neutral-700">
                        <TelLink clickSource="service_areas_phone_info" className="text-primary hover:underline">
                          03 9022 4442
                        </TelLink>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas Grid */}
        <section className="py-20 bg-neutral-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                  All Service Areas
                </h2>
                <p className="text-xl text-neutral-700 max-w-3xl mx-auto">
                  We proudly provide comprehensive dental care to patients from the following suburbs and surrounding areas:
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {serviceAreas.map((area) => (
                  <Link
                    key={area.slug}
                    to={`/locations/${area.slug}`}
                    className="group bg-white rounded-lg p-6 shadow-soft hover:shadow-hover transition-all duration-300 transform hover:scale-105 hover:-translate-y-1"
                  >
                    <div className="flex items-center">
                      <MapPin className="w-5 h-5 text-primary mr-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="text-neutral-800 font-medium group-hover:text-primary transition-colors">
                        {area.name}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                Comprehensive Dental Services
              </h2>
              <p className="text-xl text-neutral-700 mb-12">
                No matter where you're coming from in Melbourne's northern suburbs, we offer a full range of dental services:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-neutral-800 mb-4">General Dentistry</h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Comprehensive examinations & cleanings</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Dental fillings & restorations</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Root canal therapy</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Emergency dental care</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-neutral-800 mb-4">Cosmetic Dentistry</h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Teeth whitening treatments</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Dental veneers</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Crowns & bridges</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Smile makeovers</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-neutral-800 mb-4">Family Dentistry</h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Children's dentistry (CDBS)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Fluoride treatments</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Fissure sealants</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Preventive care for all ages</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-neutral-800 mb-4">Advanced Services</h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Dental implants</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Wisdom teeth removal</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Dentures & partials</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                      <span>Sedation dentistry</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-12">
                <Link
                  to="/treatments"
                  className="inline-block bg-primary text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary/90 transition-all transform hover:scale-105 shadow-soft hover:shadow-hover"
                >
                  View All Treatments
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary to-primary/90">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Book Your Appointment?
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Join hundreds of patients from across Melbourne's northern suburbs who trust Y3 Smiles Dental for their dental care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <TelLink
                  clickSource="service_areas_cta"
                  className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-neutral-100 transition-all transform hover:scale-105 shadow-soft hover:shadow-hover"
                >
                  Call 03 9022 4442
                </TelLink>
                <Link
                  to="/contact"
                  className="inline-block bg-booking-gold text-white px-8 py-4 rounded-lg font-semibold hover:bg-booking-gold/90 transition-all transform hover:scale-105 shadow-soft hover:shadow-hover"
                >
                  Book Online
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServiceAreasPage;
